<template>
  <v-dialog :value="value" v-on:input="$emit('input', $event)" max-width="1000px" persistent>
    <v-form ref="form" v-model="valid">
      <v-card>
        <v-toolbar>
          <v-toolbar-title v-if="isNew" class="grey--text text--darken-4">Add word</v-toolbar-title>
          <v-toolbar-title v-if="!isNew" class="grey--text text--darken-4">{{ model.text }}</v-toolbar-title>
        </v-toolbar>
        <v-alert :value="showErrorOnSave" type="error">{{ errorOnSave }}</v-alert>
        <v-container grid-list-md>
          <v-layout wrap>
            <v-flex xs6>
              <v-layout wrap>
                <v-flex xs12>
                  <v-text-field
                          v-model="edited.text"
                          required
                          :rules="[required]"
                          label="Word"
                          :error-messages="errorMessages"
                          :disabled="saving"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-select
                          v-model="edited.categoryIds"
                          :disabled="saving"
                          :items="categories"
                          :rules="[required]"
                          multiple
                          item-text="fullName"
                          item-value="id"
                          label="Category"
                  ></v-select>
                </v-flex>
                <v-flex xs12>
                  <AudioInput v-model="edited.pronunciation" :suggest="edited.text" style="display: none"></AudioInput>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs6>
              <v-layout wrap>
                <v-flex xs12 v-if="edited.baseWord">
                  <v-text-field
                          :value="edited.baseWord"
                          label="Base word"
                          :disabled="true"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-text-field
                          :value="english"
                          label="In English"
                          :disabled="true"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <v-text-field
                          :value="russian"
                          label="In Russian"
                          :disabled="true"
                  ></v-text-field>
                </v-flex>
                <v-flex xs12>
                  <ImageInput v-model="edited.image" :suggest="edited.text" :aspect-ratio="1" />
                </v-flex>
              </v-layout>
            </v-flex>
          </v-layout>
        </v-container>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" flat @click.native="close">Отменить</v-btn>
          <v-btn color="blue darken-1" flat @click.native="save" :loading="saving">Сохранить</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import { TranslatorApi, WordsApi } from '../common/api'
import { ADD_WORD, UPDATE_WORD } from '../store/mutation-types'
import { mapGetters, mapState } from 'vuex'
import ImageInput from './ImageInput'
import find from 'lodash/find'
import isEqual from 'lodash/isEqual'
import debounce from 'lodash/debounce'
import AudioInput from './AudioInput'

export default {
  components: { AudioInput, ImageInput },
  props: ['value', 'model'],
  data () {
    return {
      valid: false,
      saving: false,
      errorMessages: [],
      errorOnSave: 'Error has occurred while saving',
      showErrorOnSave: false,
      edited: this.defaultModel(),
      russian: '',
      english: ''
    }
  },
  computed: {
    isNew () {
      return !this.model
    },
    ...mapState([
      'categories'
    ]),
    ...mapGetters([
      'sourceLanguageId'
    ])
  },
  watch: {
    'edited.text' () {
      this.debouncedTranslateAll()
    },
    value () {
      this.showErrorOnSave = false
      this.$refs.form.resetValidation()
      if (!this.value) {
        return
      }
      if (this.isNew) {
        this.edited = this.defaultModel()
      } else {
        this.edited = { ...this.model, translation: { ...this.model.translation } }
      }
    }
  },
  created () {
    this.debouncedTranslateAll = debounce(this.translateAll, 300)
  },
  methods: {
    defaultModel () {
      return {
        text: '',
        baseWord: null,
        pronunciation: null,
        image: null,
        localizedImage: false,
        languageId: this.sourceLanguageId,
        categoryIds: []
      }
    },
    validate () {
      return this.$refs.form.validate()
    },
    close () {
      if (this.saving) {
        return
      }
      this.$emit('input', false)
    },
    async save () {
      if (this.saving) {
        return
      }
      if (!this.validate()) {
        return
      }
      this.showErrorOnSave = false
      this.saving = true
      try {
        if (!await this.checkDuplicates(this.edited)) {
          throw new Error('Disable to add duplicate')
        }

        if (this.isNew) {
          const { data } = await WordsApi.create(this.edited)
          this.$store.commit(ADD_WORD, data)
        } else {
          const { data } = await WordsApi.update(this.edited)
          this.$store.commit(UPDATE_WORD, data)
        }
        this.saving = false
        this.close()
      } catch (e) {
        this.saving = false
        this.showErrorOnSave = true
        console.error(e)
      }
    },
    required (value) {
      return !!value || 'Required.'
    },
    async checkDuplicates (word) {
      const { data } = await WordsApi.duplicates(this.$store.state.sourceDictionary, word.text)
      if (data.total === 0) {
        return true
      }

      if (data.total === 1 && data.items[0].id === word.id) {
        return true
      }

      const categories = this.categories
        .filter(({ id }) => find(data.items, { categoryId: id }) !== undefined)
        .map(cat => cat.name)

      if (confirm('Данное слово уже присутствует в категориях: ' + categories.join(', ') + '. Вы уверены что хотите добавить?')) {
        return true
      }

      return false
    },
    translateAll () {
      const getRequest = () => { return { text: this.edited.text, sourceLanguage: this.edited.languageId } }
      this.russian = null
      this.english = null

      if (this.edited.languageId !== 'ru') {
        this.translate(getRequest, 'ru')
          .then((translation) => { if (translation !== undefined) { this.russian = translation } })
          .catch(e => alert(e))
      }

      if (this.edited.languageId !== 'en') {
        this.translate(getRequest, 'en')
          .then((translation) => { if (translation !== undefined) { this.english = translation } })
          .catch(e => alert(e))
      }
    },
    async translate (getRequest, target) {
      const req = getRequest()

      if (!req.text) {
        return null
      }

      try {
        const { data } = await TranslatorApi.translate(req.text, req.sourceLanguage, target)
        if (!isEqual(req, getRequest())) {
          console.log('Skip translation, because word has been changed')
          return undefined
        }
        return data.text
      } catch (e) {
        throw e
      }
    }
  }
}
</script>
